import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { z } from 'zod';

import {
  TEALIUM_SEND_RESULTS,
  useTealium,
} from '../../tagging/tealium/TealiumProvider';
import { debugCohesion, debugCohesionError } from '../../utils/debug-logger';
import { EventMap } from './events/event-map';
import { shouldTrackOncePerPageView } from './page-view-tracking-registry';

/**
 * Analytics provider for tracking user events with Cohesion.
 *
 * @example Page Setup
 * <TealiumProvider>
 *   <CohesionProvider>
 *     <Page />
 *   </CohesionProvider>
 * </TealiumProvider>
 *
 * @example Send an event
 * const { trackEvent } = useCohesion()
 * trackEvent(EventCreators.elementClicked({ webElement: { name: "header-logo" } }))
 */

// #region JSDoc Types

const TrackEventPayloadSchema = z.object({
  name: z.string(),
  params: z.record(z.string(), z.any()),
});

/**
 * @typedef {z.infer<typeof TrackEventPayloadSchema>} TrackEventPayload
 */

/**
 * @typedef {function(TrackEventPayload): void} TrackEventFunction
 * Tracks analytics events with Cohesion
 */

/**
 * @typedef {object} CohesionContextValue
 * @property {TrackEventFunction} trackEvent - Function to track analytics events
 */

// #endregion

const CohesionContext = createContext(
  /** @type {CohesionContextValue | undefined} */ (undefined),
);

/**
 * Provider that enables safe Cohesion analytics tracking throughout the site.
 * Events are validated and automatically queued until ready.
 *
 * @param {{ children: React.ReactNode }} props
 */
export const CohesionProvider = ({ children }) => {
  const { fireLink, fireView } = useTealium();

  // Show Cohesion debugging tips once on mount
  useEffect(() => {
    debugCohesion(() => {
      console.groupCollapsed(
        '🟢 [Cohesion] Ready — click to expand for debugging tips',
      );
      console.log('🔧 Debug tools available in `window.__cohesionDebug`:');
      console.log(
        '   • pageViewTrackedKeys() - See all keys tracked this page view',
      );
      console.log('   • pageViewTrackingStats() - Get counts by event type');
      console.log(
        '   • resetPageViewTracking() - Manually reset (simulates navigation)',
      );
      console.log('');
      console.log('📡 Check if Cohesion loaded:');
      console.log('   window.__Cohesion?.initialized === true');
      console.log('');
      console.log('🔍 Look for "tagular-beam" logs or successful POST to:');
      console.log('   https://ingest.make.rvohealth.com/v2/t');
      console.groupEnd();
    });
  }, []);

  /** @type {TrackEventFunction} */
  const trackEvent = useCallback(
    (rawEvent) => {
      try {
        TrackEventPayloadSchema.parse(rawEvent);
      } catch (error) {
        debugCohesionError(() =>
          console.error(
            `❌ [${
              rawEvent?.name || 'UNKNOWN'
            }] Cannot send event; missing required properties (name/params): ${
              error.message
            }\n\nEvent: ${JSON.stringify(rawEvent, null, 2)}\n`,
          ),
        );

        return;
      }

      const { name: eventName } = rawEvent;

      /**
       * Recursively removes nullish values (`null` / `undefined`) from objects and arrays,
       * and sorts arrays to ensure consistent, deterministic payloads.
       *
       * @example
       * Input:
       * {
       *   tags: [3, 'beta', null, 'alpha', 1],
       *   tasks: [
       *     { id: null, label: 'X' },
       *     { id: '2', label: undefined }
       *   ]
       * }
       *
       * Output:
       * {
       *   tags: [1, 3, 'alpha', 'beta'],
       *   tasks: [
       *     { label: 'X' },
       *     { id: '2' }
       *   ]
       * }
       */
      const sanitizeEventParams = (data) => {
        if (data == null || typeof data !== 'object') return data;

        const stringifyForSort = (val) =>
          typeof val === 'object' ? JSON.stringify(val) : String(val);

        if (Array.isArray(data)) {
          return data
            .map(sanitizeEventParams)
            .filter((item) => item != null)
            .sort((a, b) =>
              stringifyForSort(a).localeCompare(stringifyForSort(b)),
            );
        }

        return Object.entries(data).reduce((acc, [key, value]) => {
          if (value == null) {
            debugCohesion(() =>
              console.warn(
                `[${eventName}] Skipping property "${key}" because undefined values aren't allowed (value: ${value})`,
                {
                  originalParams: rawEvent,
                  currentObject: data,
                },
              ),
            );
            return acc;
          }

          const cleanedValue =
            typeof value === 'object' ? sanitizeEventParams(value) : value;

          if (cleanedValue != null) {
            acc[key] = cleanedValue;
          }

          return acc;
        }, {});
      };

      /** @type {TrackEventPayload} */
      const event = {
        ...rawEvent,

        // Normalize params: remove nullish values and sort arrays for consistent analytics
        params: sanitizeEventParams(rawEvent.params),
      };

      if (eventName in EventMap) {
        const {
          convertToTealiumFormat,
          validateTealiumPayload,
          tealiumMethodType = 'link',
          deduplication = { scope: 'none' },
        } = EventMap[eventName];

        // Page-view deduplication: skip if this event+key was already tracked this page view
        if (deduplication.scope === 'page') {
          if (
            !shouldTrackOncePerPageView(
              eventName,
              event.params,
              deduplication.keyBuilder,
            )
          ) {
            return; // Already tracked this page view
          }
        }

        // Transform & validate event data
        const tealiumPayload = convertToTealiumFormat(event);

        if (!tealiumPayload) {
          debugCohesionError(() =>
            console.error(`❌ [${eventName}] Data transformation failed`, {
              event,
            }),
          );

          return;
        }

        try {
          validateTealiumPayload(tealiumPayload);
        } catch (error) {
          debugCohesionError(() =>
            console.error(
              `❌ [${eventName}] Validation failed: ${error.message}`,
              {
                event,
                tealiumPayload,
              },
            ),
          );
          return;
        }

        // Send to Tealium
        try {
          const tealiumMethod =
            tealiumMethodType === 'view' ? fireView : fireLink;

          // Clone the payload for logging before Tealium mutates it.
          const payloadForLogging = structuredClone(tealiumPayload);

          const sendResult = tealiumMethod(tealiumPayload);

          if (sendResult === TEALIUM_SEND_RESULTS.SENT) {
            debugCohesion(() =>
              console.log(
                `🎯 [${eventName}] Event sent to Tealium`,
                payloadForLogging,
              ),
            );
          } else if (sendResult === TEALIUM_SEND_RESULTS.QUEUED) {
            debugCohesion(() =>
              console.log(
                `⏳ [${eventName}] Event queued (Tealium's still loading...)`,
                payloadForLogging,
              ),
            );
          }
        } catch (e) {
          debugCohesionError(() =>
            console.error(
              `❌ [${eventName}] Error sending to Tealium: ${e.message}`,
              {
                method: tealiumMethodType,
                event,
                payload: tealiumPayload,
              },
            ),
          );
        }
      } else {
        debugCohesionError(() =>
          console.error(
            `❌ Event type "${eventName}" not found in \`EventMap\`. \n\nAvailable events:\n\t• ${Object.keys(
              EventMap,
            ).join('\n\t• ')}\n\n`,
          ),
        );
      }
    },
    [fireView, fireLink],
  );

  return (
    <CohesionContext.Provider value={{ trackEvent }}>
      {children}
    </CohesionContext.Provider>
  );
};

/**
 * Access Cohesion tracking functions from context
 * @returns {CohesionContextValue}
 *
 * @example
 * const { trackEvent } = useCohesion();
 *
 * const handleClick = () => {
 *   trackEvent(EventCreators.elementClicked({
 *     actionOutcome: "navigation",
 *     webElement: { name: "header-logo" }
 *   }));
 * };
 */
export function useCohesion() {
  const context = useContext(CohesionContext);

  if (!context) {
    throw new Error('useCohesion must be used within a CohesionProvider');
  }

  return context;
}

// ============================================================================
// 🔍 DEEP DIVE: How Event Tracking Works (optional reading)
// ============================================================================

/**
 * ## Data Flow:
 * **Internal (our code)**: React Components → CohesionProvider → TealiumProvider
 * **External (3rd party)**: TealiumProvider → Tealium → (Cohesion queue) → Cohesion
 *
 * ## Troubleshooting:
 * Look for "tagular-beam" logs or successful `POST https://ingest.make.rvohealth.com/v2/t`
 * network requests - these indicate Cohesion successfully received the data.
 *
 * ## Architecture: Two-Layer Event Queueing
 *
 * This system uses a queueing architecture that ensures no events are lost:
 *
 * **Layer 1 (TealiumProvider)**: Queues events until Tealium core loads (`utag.link`/`utag.view` available)
 * **Layer 2 (Cohesion snippet)**: Queues events until Cohesion fully loads in the browser
 *
 * **Result**: You can fire events anytime - they'll reach Cohesion when everything loads.
 *
 * ## Debugging Deduplication:
 * In the browser console:
 * - `window.__cohesionDebug.pageViewTrackedKeys()` - See all keys tracked this page view
 * - `window.__cohesionDebug.pageViewTrackingStats()` - Get counts by event type
 * - `window.__cohesionDebug.resetPageViewTracking()` - Manually reset (simulates page navigation)
 *
 * ## Resources
 * - [Cohesion Snippet Explained](https://rvohealth.atlassian.net/wiki/spaces/RP/pages/162859521/Cohesion+Snippet+Code+Explained)
 * - [Implementation PR](https://github.com/healthgrades/hgpro-web/pull/696)
 */
